(function (root) {
  "use strict";

  const READY_EVENT = "quntis-finder-core:ready";
  if (root.QuntisFinderCore) return;

  const WEIGHTS = Object.freeze({
    seriesPreference: 40,
    requiredCompatibility: 100,
    multiLightPairing: 25,
    webcamFriendly: 20,
    smallDesk: 16,
    autoDimming: 12,
  });

  function isRecommendable(product) {
    return product?.recommendable ?? product?.specs?.recommendable ?? true;
  }

  function productFlag(product, key, fallbackKey = key) {
    return product?.needs?.[key] === true
      || product?.specs?.[key] === true
      || product?.[fallbackKey] === true;
  }

  function requestedNeed(answers, key) {
    const needs = answers?.needs;
    return answers?.need === key
      || answers?.[key] === true
      || (Array.isArray(needs) && needs.includes(key))
      || needs?.[key] === true;
  }

  function preferredSeries(answers) {
    if (answers?.light === "rgb"
      || answers?.purpose === "gaming"
      || answers?.purpose === "streaming") return "im";
    if (answers?.purpose === "creative"
      || answers?.purpose === "crafting"
      || answers?.setup === "wide"
      || answers?.light === "task") return "studio";
    if (answers?.purpose === "calls" || answers?.light === "ambient") return "glow";
    return "focus";
  }

  function normalizeMonitorType(value) {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    if (!normalized) return "";
    if (normalized.includes("all-monitor")) return "all-monitor-types";
    if (normalized.includes("non-monitor")) return "non-monitor";
    if (normalized.includes("laptop")) return "laptop";
    if (normalized.includes("curved") && normalized.includes("flat")) return "curved-flat";
    if (normalized.includes("curved")) return "curved";
    if (normalized.includes("flat")) return "flat";
    return normalized;
  }

  function supportsSetup(product, setup) {
    if (!setup || setup === "dual" || setup === "wide") return true;
    const monitorType = normalizeMonitorType(
      product?.specs?.monitorType || product?.monitorType,
    );
    if (setup === "curved") {
      if (monitorType) return ["curved", "curved-flat", "all-monitor-types"].includes(monitorType);
      return productFlag(product, "curvedMonitor");
    }
    if (setup !== "flat") return true;
    return monitorType === ""
      || ["flat", "curved-flat", "all-monitor-types"].includes(monitorType);
  }

  function scoreProduct(product, answers) {
    const reasons = [];
    let score = 0;
    let disqualified = false;

    if (product.series === preferredSeries(answers)) {
      score += WEIGHTS.seriesPreference;
      reasons.push("seriesPreference");
    }

    if (!supportsSetup(product, answers?.setup)) {
      disqualified = true;
    } else if (answers?.setup === "curved" || answers?.setup === "flat") {
      score += WEIGHTS.requiredCompatibility;
      reasons.push("requiredCompatibility");
    }

    if (answers?.setup === "dual" && productFlag(product, "multiLightPairing", "multiLightControl")) {
      score += WEIGHTS.multiLightPairing;
      reasons.push("multiLightPairing");
    }
    if (answers?.purpose === "calls" && productFlag(product, "webcamFriendly")) {
      score += WEIGHTS.webcamFriendly;
      reasons.push("webcamFriendly");
    }
    if (requestedNeed(answers, "smallDesk") && productFlag(product, "smallDesk")) {
      score += WEIGHTS.smallDesk;
      reasons.push("smallDesk");
    }
    if (requestedNeed(answers, "autoDimming") && productFlag(product, "autoDimming")) {
      score += WEIGHTS.autoDimming;
      reasons.push("autoDimming");
    }

    return { score, reasons, disqualified };
  }

  function exactProduct(products, predicate, reason) {
    const product = products.find((candidate) => isRecommendable(candidate) && predicate(candidate));
    return product
      ? [{ ...product, score: WEIGHTS.requiredCompatibility, reasons: [reason] }]
      : [];
  }

  function recommend(answers = {}, products = []) {
    if (answers.setup === "laptop") {
      return exactProduct(
        products,
        (product) => product.sku === "LI-HY-1204-GY-QU",
        "requiredCompatibility",
      );
    }
    if (answers.purpose === "piano") {
      return exactProduct(
        products,
        (product) => product.variantCode === "S60BK",
        "requiredCompatibility",
      );
    }

    return products
      .filter(isRecommendable)
      .map((product) => ({ ...product, ...scoreProduct(product, answers) }))
      .filter((product) => !product.disqualified)
      .sort((left, right) => right.score - left.score || left.sku.localeCompare(right.sku));
  }

  function comparableValue(product, key) {
    const aliases = {
      autoDimming: "autoDimming",
      color: "color",
      curvedMonitor: "curvedMonitor",
      modelNumber: "globalModelNumber",
      monitorSize: "monitorSize",
      monitorType: "monitorType",
      multiLightPairing: "multiLightPairing",
      shape: "shape",
      smallDesk: "smallDesk",
      webcamFriendly: "webcamFriendly",
    };
    if (key === "recommendable") return isRecommendable(product);
    if (product?.[key] !== undefined) return product[key];
    if (product?.filters?.[key] !== undefined) return product.filters[key];
    if (product?.needs?.[key] !== undefined) return product.needs[key];
    return product?.specs?.[aliases[key] || key];
  }

  function equalFilterValue(actual, expected) {
    if (typeof expected === "boolean") return actual === expected;
    return String(actual ?? "").toLowerCase() === String(expected).toLowerCase();
  }

  function filter(filters = {}, products = []) {
    return products.filter((product) => Object.entries(filters).every(([key, expected]) => {
      if (expected === undefined || expected === null || expected === "" || expected === "all") return true;
      const actual = comparableValue(product, key);
      return Array.isArray(expected)
        ? expected.some((value) => equalFilterValue(actual, value))
        : equalFilterValue(actual, expected);
    }));
  }

  function supportUrl(product, locale) {
    if (!product?.sku) return "";
    const params = [`sku=${encodeURIComponent(product.sku)}`];
    if (product.series) params.push(`series=${encodeURIComponent(product.series)}`);
    if (locale) params.push(`lang=${encodeURIComponent(locale)}`);
    return `/pages/quntis-support-hub?${params.join("&")}#manuals`;
  }

  root.QuntisFinderCore = Object.freeze({
    READY_EVENT,
    WEIGHTS,
    filter,
    recommend,
    supportUrl,
  });
  if (typeof root.dispatchEvent === "function" && typeof root.CustomEvent === "function") {
    root.dispatchEvent(new root.CustomEvent(READY_EVENT));
  }
}(typeof window !== "undefined" ? window : globalThis));
